import React from 'react';

import {
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_participantMetadata,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_coachingSessionSchedules,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_upcomingCoachingSessionInstances,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_sessionTools,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_previousCoaches,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import { getFilteredListOfUpcomingInstances } from 'coaching/lib/coachingSessionInstances';
import CoachingNoUpcomingSessionsCard from 'coaching/components/CoachingHomeView/CoachingNoUpcomingSessionsCard';
import CoachingSessionsBySubjectBox from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectBox';
import CoachingSessionUpcomingInstanceRows from 'coaching/components/CoachingHomeView/CoachingSessionUpcomingInstanceRows';

import 'coaching/components/styles/CoachingSessionsBySubjectContainer.scss';

export type CoachMetadata = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_previousCoaches;
export type ParticipantMetadata = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_participantMetadata;
export type CoachingSessionSchedule = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_coachingSessionSchedules;
export type CoachingSessionInstance = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_upcomingCoachingSessionInstances;
export type CoachingSessionTools = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_sessionTools;

export type Props = {
  subjectName?: string;
  recurringSessionSchedules?: CoachingSessionSchedule[];
  participantMetadata: ParticipantMetadata;
  previousCoaches: CoachMetadata[];
  upcomingInstances?: CoachingSessionInstance[];
  sessionTools?: CoachingSessionTools;
  hasStudentInvitation: boolean;
  userUuid: string;
};

export default function CoachingSessionsBySubjectContainer(props: Props) {
  const emptyState = (
    <CoachingNoUpcomingSessionsCard
      participantUuid={props.participantMetadata.uuid}
      sessionTools={props.sessionTools}
      subjectName={props.subjectName}
      previousCoaches={props.previousCoaches}
      data-test="empty-state-card"
    />
  );
  if (!props.subjectName) {
    // if we don't have a subjectName, just show an empty box
    return (
      <CoachingSessionsBySubjectBox
        previousCoaches={props.previousCoaches}
        participantUuid={props.participantMetadata.uuid}>
        {emptyState}
      </CoachingSessionsBySubjectBox>
    );
  }

  const filteredAndOrderedInstances = getFilteredListOfUpcomingInstances(props.upcomingInstances);
  const sharedProps = {
    subjectName: props.subjectName,
    recurringSessionSchedules: props.recurringSessionSchedules,
    upcomingInstances: filteredAndOrderedInstances,
    sessionTools: props.sessionTools,
    previousCoaches: props.previousCoaches,
    participantUuid: props.participantMetadata.uuid,
  };

  return (
    <CoachingSessionsBySubjectBox {...sharedProps}>
      {filteredAndOrderedInstances && filteredAndOrderedInstances.length > 0 ? (
        <CoachingSessionUpcomingInstanceRows
          data-test="upcoming-instance-rows"
          instances={filteredAndOrderedInstances}
          recurringScheduleUuids={props.recurringSessionSchedules?.map(s => s.uuid) ?? []}
          defaultCoach={props.recurringSessionSchedules?.[0]?.coach ?? props.previousCoaches[0]}
          userUuid={props.userUuid}
          sessionTools={props.sessionTools}
          subjectName={props.subjectName}
          participantMetadata={props.participantMetadata}
          hasStudentInvitation={props.hasStudentInvitation}
        />
      ) : (
        emptyState
      )}
    </CoachingSessionsBySubjectBox>
  );
}
